import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { 
  Bot, 
  Users, 
  Newspaper, 
  Eye, 
  Heart, 
  MessageCircle,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Activity
} from 'lucide-react';
import './Dashboard.css';

function Dashboard() {
  const [stats, setStats] = useState({
    activeBots: 0,
    totalUsers: 0,
    totalPosts: 0,
    totalViews: 0,
    totalLikes: 0,
    totalComments: 0
  });
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      // Параллельная загрузка данных
      const [botsRes, totalsRes, servicesRes] = await Promise.all([
        api.get('/api/admin/bots/status').catch(() => ({ data: { bots: [] } })),
        api.get('/api/admin/analytics/totals').catch(() => ({ data: { data: {} } })),
        api.get('/api/admin/services/status').catch(() => ({ data: { services: [] } }))
      ]);

      // Обработка ботов
      const bots = botsRes.data.bots || [];
      const activeBots = bots.filter(b => b.enabled && b.status === 'running').length;

      // Обработка статистики
      const totals = totalsRes.data.data || {};

      setStats({
        activeBots,
        totalUsers: totals.users || 0,
        totalPosts: totals.posts || 0,
        totalViews: totals.views || 0,
        totalLikes: totals.likes || 0,
        totalComments: totals.comments || 0
      });

      // Обработка сервисов
      setServices(servicesRes.data.services || []);
    } catch (error) {
      console.error('Ошибка загрузки Dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await loadDashboard();
    setRefreshing(false);
  };

  const getServiceIcon = (status) => {
    switch (status) {
      case 'running':
        return <CheckCircle size={18} className="status-icon running" />;
      case 'error':
        return <AlertCircle size={18} className="status-icon error" />;
      default:
        return <XCircle size={18} className="status-icon stopped" />;
    }
  };

  const getServiceStatus = (status) => {
    switch (status) {
      case 'running':
        return { text: 'Работает', class: 'badge-success' };
      case 'error':
        return { text: 'Ошибка', class: 'badge-error' };
      default:
        return { text: 'Остановлен', class: 'badge-neutral' };
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <span>Загрузка...</span>
      </div>
    );
  }

  return (
    <div className="dashboard fade-in">
      <div className="page-header">
        <h1 className="page-title">Панель управления</h1>
        <button 
          className={`btn btn-secondary ${refreshing ? 'refreshing' : ''}`}
          onClick={refreshData}
          disabled={refreshing}
        >
          <RefreshCw size={18} className={refreshing ? 'spin' : ''} />
          Обновить
        </button>
      </div>

      {/* Статистика */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon green">
            <Bot size={28} />
          </div>
          <div className="stat-info">
            <h3>{stats.activeBots}</h3>
            <p>Активных ботов</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon blue">
            <Users size={28} />
          </div>
          <div className="stat-info">
            <h3>{stats.totalUsers.toLocaleString()}</h3>
            <p>Пользователей</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon purple">
            <Newspaper size={28} />
          </div>
          <div className="stat-info">
            <h3>{stats.totalPosts.toLocaleString()}</h3>
            <p>Новостей</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon orange">
            <Eye size={28} />
          </div>
          <div className="stat-info">
            <h3>{stats.totalViews.toLocaleString()}</h3>
            <p>Просмотров</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon red">
            <Heart size={28} />
          </div>
          <div className="stat-info">
            <h3>{stats.totalLikes.toLocaleString()}</h3>
            <p>Лайков</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon cyan">
            <MessageCircle size={28} />
          </div>
          <div className="stat-info">
            <h3>{stats.totalComments.toLocaleString()}</h3>
            <p>Комментариев</p>
          </div>
        </div>
      </div>

      {/* Статус сервисов */}
      <div className="dashboard-grid">
        <div className="card services-card">
          <div className="card-header">
            <h3 className="card-title">
              <Activity size={20} />
              Статус сервисов
            </h3>
            <button className="btn btn-ghost btn-sm" onClick={refreshData}>
              <RefreshCw size={16} />
            </button>
          </div>
          <div className="services-list">
            {services.length > 0 ? (
              services.map((service, index) => {
                const status = getServiceStatus(service.status);
                return (
                  <div key={index} className="service-item">
                    <div className="service-info">
                      {getServiceIcon(service.status)}
                      <div className="service-details">
                        <span className="service-name">{service.name}</span>
                        <span className="service-port">Порт {service.port}</span>
                      </div>
                    </div>
                    <span className={`badge ${status.class}`}>
                      <span className={`status-dot ${service.status === 'running' ? 'online' : 'offline'}`}></span>
                      {status.text}
                    </span>
                  </div>
                );
              })
            ) : (
              <div className="empty-state">
                <p>Нет данных о сервисах</p>
              </div>
            )}
          </div>
        </div>

        <div className="card activity-card">
          <div className="card-header">
            <h3 className="card-title">
              <Activity size={20} />
              Последняя активность
            </h3>
          </div>
          <div className="activity-list">
            <div className="empty-state">
              <Activity size={40} className="empty-state-icon" />
              <p>Нет активности</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
